import React from "react";
import { motion } from "framer-motion";
import "./Sustainability.css";

const Sustainability = () => {

  const initiatives = [
    {
      title: "Water Conservation & Effluent Treatment",
      description: "Effluent Treatment Plants return treated water from every wash cycle, and modern laundry equipment cuts the water each garment needs in the first place. Denim finishing is the most water-intensive stage in this industry, which is why it receives the closest monitoring.",
      image: "/img/Sustainability/ETP.jpeg"
    },
    {
      title: "Renewable Energy Integration",
      description: "Rooftop solar generates roughly 200 MWh a year for the factory, and repeated energy audits keep pushing consumption per garment down. In a country where power is both costly and unreliable, this serves continuity of supply as much as it does emissions.",
      image: "/img/Sustainability/Sunset on Solar Panels.png"
    },
    {
      title: "Ethical Labor & Community",
      description: "Safe floors, fair pay, and audited working conditions — for our own 500-strong team and for every supplier producing under our name. Retention is high as a result, and the standard of work reflects it.",
      image: "/img/Sustainability/Screenshot 2022-07-25 at 3_08_40 PM.png"
    },
    {
      title: "Responsible Raw Materials",
      description: "Recycled and organic fibers wherever the product allows, backed by supply-chain audits that confirm the fiber is what the paperwork says it is. Certification carries weight only where it is independently verified.",
      image: "/img/Sustainability/11062b_84fd4f3425564c84818db9f6c5a1f071~mv2_d_5616_3744_s_4_2.png"
    }
  ];

  const certifications = [
    { name: "OEKO-TEX", image: "/img/Logos/oeko.png", description: "STANDARD 100 Certified" },
    { name: "GOTS", image: "/img/Logos/gots.png", description: "Global Organic Textile Standard" },
    { name: "GRS", image: "/img/Logos/grs.png", description: "Global Recycled Standard" },
    { name: "BCI", image: "/img/Logos/bci.png", description: "Better Cotton Initiative" },
    { name: "BSCI", image: "/img/Logos/BSCI-Certification-amfori.png", description: "Business Social Compliance" },
    { name: "Sedex", image: "/img/Logos/sedex.png", description: "Ethical Trade Membership" }
  ];

  const sdgGoals = [
    { num: "06", title: "Clean Water", desc: "Effluent treatment and lower wash-cycle water use, built into daily production." },
    { num: "07", title: "Clean Energy", desc: "Rooftop solar generation and recurring energy audits across our operations." },
    { num: "12", title: "Responsible Consumption", desc: "Circular thinking, with recycled and organic materials used wherever the product allows." },
    { num: "13", title: "Climate Action", desc: "A measured — and shrinking — industrial carbon footprint." },
    { num: "17", title: "Partnerships", desc: "Only partners who clear the same ethical bar we set ourselves." }
  ];

  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
  };

  return (
    <div className="sustainability-modern-page">

      {/* ── Compact Banner Hero ── */}
      <section
        className="banner-hero"
        style={{ backgroundImage: `url('/img/Sustainability/Sunset on Solar Panels.png')` }}
      >
        <div className="banner-overlay"></div>
        <div className="container banner-content">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <span className="copper-eyebrow">Environment &amp; Ethics</span>
            <h1 className="hero-title-bold">
              Responsibility, <span className="hero-italic">Measured.</span>
            </h1>
          </motion.div>
        </div>
      </section>

      {/* ── High-Contrast Overview (The Framework) ── */}
      <section className="framework-overview-section">
        <div className="container split-layout">
          <motion.div className="overview-text" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <h2>People. Planet. Profit.</h2>
            <div className="copper-divider"></div>
            <p className="lead-text">
              At Softwood, sustainability is operational, not ornamental: solar on our roofs, water treatment on our own site, and certified materials in the products we make. Each is open to inspection, measurement, or documentary proof.
            </p>
            <p>
              Suppliers are held to the same bar, and only those who pass our audits produce under our name. Independent certification, from OEKO-TEX and GOTS through to amfori BSCI and Sedex, verifies the claims on this page. We rely on independent verification rather than on our own assurances.
            </p>
          </motion.div>
          <motion.div className="cert-cluster" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <h3>Verified Compliance</h3>
            <div className="cert-flex">
              {certifications.map((cert, index) => (
                <div className="cert-badge" key={index}>
                  <img src={cert.image} alt={cert.name} />
                  <span>{cert.name}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Cinematic Video Section ── */}
      <section className="sustainability-video-section">
        <div className="container">
          <motion.div className="section-header text-center" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <h2>The Journey in Motion</h2>
            <div className="copper-divider center-div"></div>
          </motion.div>
          <motion.div
            className="video-wrapper"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <iframe
              src="https://www.youtube.com/embed/3iSN_Xu5VGQ?start=8&rel=0&modestbranding=1"
              title="Softwood Pvt. Ltd. Sustainability"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </motion.div>
        </div>
      </section>

      {/* ── Editorial Alternating Initiatives ── */}
      <section className="initiatives-editorial-section">
        <div className="container">
          <motion.div className="section-header text-center" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <h2>Core Initiatives</h2>
            <p className="subtitle">Four programs built into daily operations rather than added afterwards. Each began as a commercial or regulatory requirement, which is why each has endured.</p>
          </motion.div>

          <div className="editorial-rows">
            {initiatives.map((init, index) => {
              const isEven = index % 2 !== 0;
              return (
                <div className={`adv-row ${isEven ? 'reverse-row' : ''}`} key={index}>
                  <motion.div className="adv-img-col" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.4 }} variants={fadeUp}>
                    <div className="img-reveal-wrapper">
                      <img src={init.image} alt={init.title} />
                    </div>
                  </motion.div>

                  <motion.div className="adv-text-col" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.4 }} variants={fadeUp}>
                    <div className="glass-card">
                      <span className="adv-number">0{index + 1}</span>
                      <h3>{init.title}</h3>
                      <p>{init.description}</p>
                    </div>
                  </motion.div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Interactive SDG Grid ── */}
      <section className="sdg-grid-section">
        <div className="container">
          <motion.div className="section-header" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <span className="copper-eyebrow">Global Alignment</span>
            <h2>United Nations Sustainable <br />Development Goals</h2>
          </motion.div>

          <motion.div
            className="sdg-grid"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
          >
            {sdgGoals.map((goal, index) => (
              <motion.div className="sdg-card" key={index} variants={fadeUp}>
                <span className="sdg-num">{goal.num}</span>
                <h4>{goal.title}</h4>
                <p>{goal.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Reused High-Contrast CTA ── */}
      <section className="clean-cta">
        <div className="container">
          <motion.div
            className="cta-box"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            style={{ backgroundImage: `url('/img/Sustainability/Sunset on Solar Panels.png')` }}
          >
            <div className="cta-dark-overlay"></div>
            <div className="cta-content-wrapper">
              <h2>Build a Responsible Supply Chain.</h2>
              <a href="/contact" className="cta-link">Partner With Us &rarr;</a>
            </div>
          </motion.div>
        </div>
      </section>

    </div>
  );
};

export default Sustainability;